// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a colour per pin, so a pin and its trace are the same thing.
 * The first six of AYNI 2D's eight (`src/shared/measurement-colors.ts`),
 * since the Data tab holds six pins at most (four until 2026-10-09).
 *
 * They sit on top of the data, so they are light and high-chroma and the
 * marker carries a dark ring; they come from the Okabe-Ito set, so people
 * who cannot tell red from green can still tell them apart; and a colour is
 * assigned once and kept with the pin, so removing one pin never recolours
 * another's trace mid-flight.
 */

export const MEASUREMENT_COLORS = [
  '#56B4E9', // sky blue
  '#E69F00', // orange
  '#00C08B', // bluish green
  '#E38FC4', // reddish purple
  '#F0E442', // yellow
  '#FF7A47', // vermillion
] as const

/** The least-used colour, earliest first on ties. */
export function nextMeasurementColor(inUse: ReadonlyArray<string | null | undefined>): string {
  const counts = new Map<string, number>(MEASUREMENT_COLORS.map((c) => [c, 0]))
  for (const color of inUse) {
    if (color && counts.has(color)) counts.set(color, counts.get(color)! + 1)
  }
  let best: string = MEASUREMENT_COLORS[0]
  let bestCount = Infinity
  for (const color of MEASUREMENT_COLORS) {
    const count = counts.get(color) ?? 0
    if (count < bestCount) {
      best = color
      bestCount = count
    }
  }
  return best
}
